import { useEffect, useMemo } from "react";
import { IFormSection, TFormHookParams } from "../../types/common";
import {
  API_PARKINGS_ALL,
  API_SERVICE_TYPES_MOBILE,
  API_VEHICLE_CLAMPINGS,
  API_VEHICLE_CLAMPINGS_UNCLAMP,
} from "../../utils/apis";
import useGet from "../common/useGet";
import { CLAMPED, NOTIFIED, UNCLAMPED } from "../../utils/constants";
import {
  createColumn,
  createClampAction,
  createUnclampAction,
  createViewAction,
  formatDateTime,
  formatPaymentStatus,
  getRegister,
  numberFormat,
} from "../../utils/helpers";
import { FormActions } from "../../components/common/FormActions";
import { useLocation, useSearchParams } from "react-router-dom";
import { URL_VEHICLE_CLAMPING } from "../../utils/urls";
import { BooleanTags } from "../../components/common/registerUtils";
import BuildOutlinedIcon from "@mui/icons-material/BuildOutlined";

export const useVehicleClamping = () => {
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const actionType = useMemo(() => {
    return (
      location.state?.filterParams?.action_type ||
      searchParams.get("action_type")
    );
  }, [location.state, searchParams]);

  // Fetch service types for clamp actions
  const { loadData: loadServiceTypes, data: serviceTypes } = useGet();

  useEffect(() => {
    loadServiceTypes({ api: API_SERVICE_TYPES_MOBILE });
  }, []);

  const serviceOptions = serviceTypes
    ? serviceTypes.map((serviceValue: any) => ({
        value: serviceValue.id,
        label: serviceValue.name,
      }))
    : [];

  const getColumnsByType = (type: string) => {
    switch (type) {
      case CLAMPED:
        return [
          createColumn("parking_name", true, "Parking Name"),
          createColumn("city_name", true, "City"),
          createColumn("car_number", true, "Car Number"),
          createColumn("clamping_fee", true, "Clamping Fee"),
          createColumn("created_at", true, "Created At"),
          createColumn("updated_at", true, "Updated At"),
          createColumn("service_type_name", true, "Service Type Name"),
          createColumn("is_active", true, "Active"),
          createColumn("actions", false),
        ];

      case NOTIFIED:
        return [
          createColumn("parking_name", true, "Parking Name"),
          createColumn("city_name", true, "City"),
          createColumn("car_number", true, "Car Number"),
          createColumn("clamping_fee", true, "Clamping Fee"),
          createColumn("service_type_name", true, "Service Type Name"),
          createColumn("is_active", true, "Active"),
          createColumn("actions", false),
        ];
      case UNCLAMPED:
        return [
          createColumn("parking_name", true, "Parking Name"),
          createColumn("city_name", true, "City"),
          createColumn("car_number", true, "Car Number"),
          createColumn("clamping_fee", true, "Clamping Fee"),
          createColumn("service_type_name", true, "Service Type Name"),
          createColumn("is_active", true, "Active"),
          // createColumn("actions", false),
        ];
      default:
        return [];
    }
  };

  const getActionConfig = (id: string, carNumber: string) => {
    if (actionType === NOTIFIED) {
      return {
        url: `${API_VEHICLE_CLAMPINGS}${id}/`,
        method: "PATCH",
        params: { action_type: CLAMPED },
        label: "Clamp",
      };
    }

    if (actionType === CLAMPED) {
      return {
        url: API_VEHICLE_CLAMPINGS_UNCLAMP,
        method: "POST",
        params: { action_type: UNCLAMPED, car_number: carNumber },
        label: "Unclamp",
      };
    }

    // if (actionType === UNCLAMPED) {
    //   return {
    //     url: `${API_VEHICLE_CLAMPINGS}${id}/`,
    //     method: "PATCH",
    //     params: { action_type: CLAMPED },
    //     label: "Clamp",
    //   };
    // }

    return {
      url: "",
      method: "POST",
      params: {},
      label: "Action",
    };
  };

  const tableColumns = () => [...getColumnsByType(actionType)];

  const tableData = (data: any[] = []) => {
    if (!Array.isArray(data)) return [];

    return data.map((item) => {
      const actionConfig = getActionConfig(item.id, item.car_number);

      // Determine which action creator to use
      const actionCreator =
        actionType === CLAMPED ? createUnclampAction : createClampAction;

      return {
        ...item,
        is_active: <BooleanTags status={item?.is_active} />,
        clamping_payment_status: formatPaymentStatus(
          item?.clamping_payment_status,
        ),
        clamping_fee: numberFormat(item?.clamping_fee),
        updated_at: formatDateTime(item?.updated_at),
        created_at: formatDateTime(item?.created_at),
        actions: (
          <FormActions
            actions={[
              actionCreator(
                actionConfig.url,
                <BuildOutlinedIcon />,
                actionConfig.method,
                actionConfig.params,
              ),
              createViewAction(URL_VEHICLE_CLAMPING, item?.id),
            ]}
            serviceOptions={serviceOptions}
          />
        ),
      };
    });
  };

  const viewData = (item: any) => {
    return {
      ...item,
      clamping_payment_status: formatPaymentStatus(
        item?.clamping_payment_status,
      ),
      clamping_fee: numberFormat(item?.clamping_fee),
      created_by:
        item?.created_by["first_name"] + " " + item?.created_by["last_name"],
      created_at: formatDateTime(item?.created_at),
      updated_at: formatDateTime(item?.updated_at),
    };
  };

  const activityFilter = "clamping";

  return {
    tableColumns,
    tableData,
    viewData,
    activityFilter,
  };
};

export const useVehicleClampingForm = ({
  updateFormValues,
  form,
}: TFormHookParams = {}) => {
  const [searchParams] = useSearchParams();
  const actionType = searchParams.get("action_type") || "";
  const carNumber = searchParams.get("car_number") || "";

  const {
    loadData: loadServiceTypes,
    loading: loadingServiceTypes,
    data: serviceTypes,
  } = useGet();

  useEffect(() => {
    loadServiceTypes({ api: API_SERVICE_TYPES_MOBILE });
  }, []);

  const serviceOptions = serviceTypes
    ? serviceTypes.map((serviceValue: any) => ({
        value: serviceValue.id,
        label: serviceValue.name,
      }))
    : [];

  // Set default values for form submission
  const defaultValues = useMemo(
    () => ({
      action_type: actionType,
      car_number: carNumber,
    }),
    [actionType, carNumber],
  );
  const {
    loadData: loadParkings,
    loading: loadingParkings,
    data: parkings,
  } = useGet();

  useEffect(() => {
    if (updateFormValues) {
      updateFormValues();
    }
  }, [updateFormValues]);

  useEffect(() => {
    loadParkings({ api: API_PARKINGS_ALL });
  }, []);

  const sections: IFormSection[] = [
    {
      title: "Vehicle Clamping",
      fields: [
        {
          field_name: "action_type",
          label: "Action Type",
          type: "select",
          props: {
            required: true,
            placeholder: "Select action type",
            defaultValue: actionType,
            options: [
              { value: CLAMPED, label: "Clamp" },
              { value: NOTIFIED, label: "Notify" },
            ],
          },
        },
        {
          field_name: "car_number",
          label: "Car Number",
          type: "text",
          props: {
            required: true,
            placeholder: "Enter car number",
            defaultValue: carNumber,
          },
        },
        {
          field_name: "parking",
          label: "Parking ID",
          type: "select",
          props: {
            required: true,
            placeholder: "Select parking ID",
            options:
              parkings?.map((item: any) => ({
                value: item.id,
                label: item.name,
              })) || [],
          },
        },
        {
          field_name: "service_type",
          label: "Service Type",
          type: "select",
          props: {
            required: false,
            placeholder: "Enter service type",
            options: serviceOptions,
          },
        },
      ],
    },
  ];

  // clamping/clamped?action_type=clamped

  const redirect = getRegister(
    `${URL_VEHICLE_CLAMPING}/${form.action_type}?action_type=${form.action_type}`,
  );

  return {
    sections,
    loadingParkings,
    loadingServiceTypes,
    parkings,
    redirect,
    defaultValues,
  };
};
